import { useState, useRef } from "react";
import { t } from "i18next";
import { DemandForm } from "../../modals/demand_modal/components/DemandForm";
import { createDemand } from "../../../api/demands/createDemand";
import ErrorModal from "../../modals/error_modal/ui/Main";
import OpenModalButton from '../../elements/OpenModalButton/ui/OpenModalButton';
import { units } from "../constants/units";
import DropdownUnit from "../../../common/components/DropdownUnit";
import CharacteristicsFields from '../../../common/components/CharacteristicsFields';

import warning_icon from '../../../assets/icons/WarningCircle.svg';

const BULK_UPLOAD_MAX_FILE_BYTES = 10 * 1024 * 1024;
const BULK_UPLOAD_MAX_ROWS = 500;
const BULK_UPLOAD_ALLOWED_EXT = [".xlsx", ".csv", ".tsv"];

export function AddForm(
    handleImagesChange, handleVideoLinkChange, handleAnnouncementInputChange, isSubmitted, customTags,
    handleCustomTagsChange, handleAddTag, handleRemoveTag, tagsArray, handleSelectCategory, handleDescriptionChange,
    priceType, setPriceType, price, setPrice, handleVolumeChange, handlePublish, isLoading, handlePreview,
    selectedSubcategory, volumeUnit, announcementName, description, aiSuggestions, onSuggestionClick, onAIGenerate,
    onUpdateSuggestions, activeTab, setActiveTab, selectedCategory, jsonData, setJsonData, isDealer, dealerRole,
    stockQuantity, setStockQuantity, minOrderQuantity, setMinOrderQuantity, minOrderPrice, setMinOrderPrice,
    canDeliver, setCanDeliver,
    deliveryCity, setDeliveryCity, deliveryDays, setDeliveryDays, deliveryTime, setDeliveryTime,
    deliveryVolume, setDeliveryVolume, deliveryDuration, setDeliveryDuration,
    toggleDeliveryDay,
    weekDays
) {
    const [images, setImages] = useState([]);
    const [previewUrls, setPreviewUrls] = useState([]);
    const fileInputRef = useRef(null);

    // Demand form state
    const [demandTitle, setDemandTitle] = useState('');
    const [demandDescription, setDemandDescription] = useState('');
    const [demandMaxPrice, setDemandMaxPrice] = useState('');
    const [demandSelectedCategory, setDemandSelectedCategory] = useState('');
    const [demandSelectedSubcategory, setDemandSelectedSubcategory] = useState('');
    const [demandKeywords, setDemandKeywords] = useState('');
    const [demandKeywordsArray, setDemandKeywordsArray] = useState([]);
    const [demandMeasurementUnit, setDemandMeasurementUnit] = useState(null);
    const [demandQuantity, setDemandQuantity] = useState('');
    const [demandIsSubmitted, setDemandIsSubmitted] = useState(false);
    const [demandIsLoading, setDemandIsLoading] = useState(false);
    const [showDemandErrorModal, setShowDemandErrorModal] = useState(false);
    const [demandErrorMessage, setDemandErrorMessage] = useState('');
    const [demandJsonData, setDemandJsonData] = useState({});

    // Video link
    const [videoLink, setVideoLink] = useState('');
    const [videoError, setVideoError] = useState('');

    // Bulk upload state
    const [bulkFile, setBulkFile] = useState(null);
    const bulkFileInputRef = useRef(null);

    const isValidYouTubeUrl = (url) => {
        if (!url) return true; // Empty is valid (optional field)
        const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
        return youtubeRegex.test(url);
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const newImages = [...images, ...files].slice(0, 10);
        setImages(newImages);

        const newPreviewUrls = files.map(file => URL.createObjectURL(file));
        setPreviewUrls(prev => [...prev, ...newPreviewUrls].slice(0, 10));

        handleImagesChange(newImages);
    };

    const removeImage = (index) => {
        const newImages = images.filter((_, i) => i !== index);
        const newPreviewUrls = previewUrls.filter((_, i) => i !== index);
        setImages(newImages);
        setPreviewUrls(newPreviewUrls);
        handleImagesChange(newImages);
    };

    const handleVideoChange = (e) => {
        const value = e.target.value;
        setVideoLink(value);
        if (value && !isValidYouTubeUrl(value)) {
            setVideoError(t('youtubeOnlyError'));
        } else {
            setVideoError('');
        }
        handleVideoLinkChange && handleVideoLinkChange(e);
    };

    const validateDemandForm = () => {
        const errors = [];
        if (!demandTitle.trim()) errors.push(t('demand_title_required'));
        if (!demandDescription.trim()) errors.push(t('description_required'));
        if (!demandSelectedCategory || !demandSelectedSubcategory) errors.push(t('category_required'));
        if (!demandMeasurementUnit) errors.push(t('measurement_unit_required'));
        if (!demandQuantity || parseFloat(demandQuantity) <= 0) errors.push(t('quantity_required'));
        return errors;
    };

    const handleDemandSubmit = async () => {
        setDemandIsSubmitted(true);
        const errors = validateDemandForm();
        if (errors.length > 0) {
            setDemandErrorMessage(errors.join(', '));
            setShowDemandErrorModal(true);
            return;
        }

        setDemandIsLoading(true);
        try {
            const accessToken = localStorage.getItem("access_token");
            if (!accessToken) throw new Error(t('authentication_required'));

            const unitMapping = {
                'piece': 'PIECE', 'kilogram': 'KILOGRAM', 'ton': 'TON', 'liter': 'LITER',
                'hectare': 'HECTARE', 'square_meter': 'SQUARE_METER', 'cubic_meter': 'CUBIC_METER',
                'bag': 'BAG', 'centner': 'CENTNER', 'head': 'HEAD', 'pair': 'PAIR', 'meter': 'METER', 'box': 'BOX'
            };
            const unitForServer = unitMapping[demandMeasurementUnit] || 'PIECE';

            const demandData = {
                title: demandTitle.trim(),
                description: demandDescription.trim(),
                max_price: demandMaxPrice ? parseFloat(demandMaxPrice) : null,
                currency: "KZT",
                category_id: parseInt(demandSelectedCategory),
                subcategory_id: parseInt(demandSelectedSubcategory),
                user_location_id: null,
                keywords: demandKeywordsArray.length > 0 ? demandKeywordsArray : demandTitle.trim().split(/\s+/).filter(w => w.length > 2),
                measurement_unit: unitForServer,
                quantity: parseFloat(demandQuantity),
                json_data: demandJsonData && Object.keys(demandJsonData).length > 0 ? demandJsonData : undefined,
            };

            const response = await createDemand(demandData, accessToken);
            if (response.demand_id) {
                setDemandTitle('');
                setDemandDescription('');
                setDemandMaxPrice('');
                setDemandSelectedCategory('');
                setDemandSelectedSubcategory('');
                setDemandKeywords('');
                setDemandKeywordsArray([]);
                setDemandMeasurementUnit(null);
                setDemandQuantity('');
                setDemandIsSubmitted(false);
                alert(t('demand_created_successfully'));
            }
        } catch (error) {
            setDemandErrorMessage(error.message || t('error_creating_demand'));
            setShowDemandErrorModal(true);
        } finally {
            setDemandIsLoading(false);
        }
    };

    if (activeTab === 'bulk') {
        return (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('bulkUploadTitle')}</h3>
                <p className="text-gray-500 text-sm mb-6">{t('bulkUploadDesc')}</p>

                <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-[#147F26] transition-colors">
                    <input
                        ref={bulkFileInputRef}
                        type="file"
                        accept=".xlsx,.csv,.tsv"
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) setBulkFile(file);
                        }}
                        className="hidden"
                    />
                    <button
                        onClick={() => bulkFileInputRef.current?.click()}
                        className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
                    >
                        {t('selectFile')}
                    </button>
                    {bulkFile && (
                        <p className="mt-4 text-sm text-gray-600">{bulkFile.name}</p>
                    )}
                </div>
            </div>
        );
    }

    if (activeTab === 'demand') {
        return (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <DemandForm
                    title={demandTitle}
                    setTitle={setDemandTitle}
                    description={demandDescription}
                    setDescription={setDemandDescription}
                    maxPrice={demandMaxPrice}
                    setMaxPrice={setDemandMaxPrice}
                    selectedCategory={demandSelectedCategory}
                    setSelectedCategory={setDemandSelectedCategory}
                    selectedSubcategory={demandSelectedSubcategory}
                    setSelectedSubcategory={setDemandSelectedSubcategory}
                    keywords={demandKeywords}
                    setKeywords={setDemandKeywords}
                    keywordsArray={demandKeywordsArray}
                    setKeywordsArray={setDemandKeywordsArray}
                    measurementUnit={demandMeasurementUnit}
                    setMeasurementUnit={setDemandMeasurementUnit}
                    quantity={demandQuantity}
                    setQuantity={setDemandQuantity}
                    isSubmitted={demandIsSubmitted}
                    jsonData={demandJsonData}
                    setJsonData={setDemandJsonData}
                />
                <button
                    onClick={handleDemandSubmit}
                    disabled={demandIsLoading}
                    className="mt-6 w-full py-3.5 bg-[#147F26] hover:bg-[#0f6820] text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
                >
                    {demandIsLoading ? t('loading') : t('publishDemand')}
                </button>
                <ErrorModal
                    showLogoutModal={showDemandErrorModal}
                    setShowLogoutModal={setShowDemandErrorModal}
                    message={demandErrorMessage}
                />
            </div>
        );
    }

    const isDealerTab = activeTab === 'dealer';

    return (
        <div className="space-y-6">
            {/* Dealer Info Banner */}
            {isDealerTab && (
                <div className="bg-gradient-to-r from-[#147F26]/10 to-[#7AB30E]/10 border border-[#147F26]/30 rounded-xl px-5 py-4 flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#147F26] rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#147F26]/30">
                        <svg className="w-6 h-6 text-white" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.175 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.05 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.287-3.957z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="font-semibold text-[#147F26] text-lg">{t('dealerAdBannerTitle')}</h3>
                        <p className="text-sm text-[#147F26]/70">{t('dealerAdBannerDesc')}</p>
                    </div>
                </div>
            )}

            {/* Section 1: Photos */}
            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 bg-[#147F26]/10 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-[#147F26]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <h2 className="text-lg font-semibold text-[#147F26]">{t('photos')}</h2>
                </div>
                <p className="text-sm text-[#7AB30E] mb-5">{t('photosHint')}</p>

                <div className="flex flex-wrap gap-4">
                    {/* Add Image Button */}
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-28 h-28 lg:w-32 lg:h-32 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center hover:border-[#147F26] hover:bg-[#147F26]/5 transition-all group"
                    >
                        <svg className="w-8 h-8 text-gray-400 group-hover:text-[#147F26] transition-colors mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <span className="text-xs text-gray-500 group-hover:text-[#147F26]">{t('addPhoto')}</span>
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                    />

                    {/* Preview Images */}
                    {previewUrls.map((url, index) => (
                        <div key={index} className="relative w-28 h-28 lg:w-32 lg:h-32 group">
                            <img
                                src={url}
                                alt={`Preview ${index + 1}`}
                                className="w-full h-full object-cover rounded-xl"
                            />
                            <button
                                onClick={() => removeImage(index)}
                                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                            >
                                ×
                            </button>
                            {index === 0 && (
                                <span className="absolute bottom-2 left-2 bg-[#147F26] text-white text-[10px] font-medium px-2 py-0.5 rounded-full">
                                    {t('main')}
                                </span>
                            )}
                        </div>
                    ))}
                </div>

                {/* Video Link */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                    <label className="block text-sm font-medium text-[#147F26] mb-2">{t('youtubeVideoLink')}</label>
                    {videoError && (
                        <div className="flex items-center gap-1 mb-2 text-red-500 text-sm">
                            <img src={warning_icon} alt="" className="w-4 h-4" />
                            <span>{videoError}</span>
                        </div>
                    )}
                    <input
                        type="url"
                        value={videoLink}
                        onChange={handleVideoChange}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className={`w-full px-4 py-3 rounded-xl border outline-none transition-all ${
                            videoError
                                ? 'border-red-400 focus:border-red-400 bg-red-50'
                                : 'border-gray-200 focus:border-[#147F26] focus:ring-2 focus:ring-[#147F26]/20'
                        }`}
                    />
                </div>
            </section>

            {/* Section 2: Basic Info */}
            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-5">
                    <div className="w-8 h-8 bg-[#147F26]/10 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-[#147F26]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <h2 className="text-lg font-semibold text-[#147F26]">{t('basicInfo')}</h2>
                </div>

                {/* Title */}
                <div className="mb-5">
                    <label className="block text-sm font-medium text-[#147F26] mb-2">
                        {t('adTitle')} <span className="text-red-500">*</span>
                    </label>
                    {isSubmitted && !announcementName && (
                        <div className="flex items-center gap-1 mb-2 text-red-500 text-sm">
                            <img src={warning_icon} alt="" className="w-4 h-4" />
                            <span>{t('enterTitle')}</span>
                        </div>
                    )}
                    <input
                        type="text"
                        value={announcementName}
                        onChange={(e) => handleAnnouncementInputChange(e.target.value)}
                        placeholder={t('titlePlaceholder')}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#147F26] focus:ring-2 focus:ring-[#147F26]/20 outline-none transition-all"
                    />
                </div>

                {/* Category */}
                <div className="mb-5">
                    <label className="block text-sm font-medium text-[#147F26] mb-2">
                        {t('selectCategory')} <span className="text-red-500">*</span>
                    </label>
                    {isSubmitted && !selectedSubcategory && (
                        <div className="flex items-center gap-1 mb-2 text-red-500 text-sm">
                            <img src={warning_icon} alt="" className="w-4 h-4" />
                            <span>{t('selectCategory')}</span>
                        </div>
                    )}
                    <OpenModalButton
                        type="category"
                        onSelect={handleSelectCategory}
                        selectedCategoryId={selectedCategory}
                        selectedSubcategoryId={selectedSubcategory}
                    />
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-[#147F26] mb-2">
                        {t('description')} <span className="text-red-500">*</span>
                    </label>
                    {isSubmitted && !description && (
                        <div className="flex items-center gap-1 mb-2 text-red-500 text-sm">
                            <img src={warning_icon} alt="" className="w-4 h-4" />
                            <span>{t('enterDescription')}</span>
                        </div>
                    )}

                    {/* AI Generate Button */}
                    <div className="mb-3">
                        <button
                            onClick={() => onAIGenerate && onAIGenerate(announcementName)}
                            disabled={!announcementName || announcementName.trim().length < 3}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-[#147F26]/10 hover:bg-[#147F26]/20 disabled:opacity-50 disabled:cursor-not-allowed text-[#147F26] rounded-lg text-sm font-medium transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            {t('aiAutoFill')}
                        </button>
                    </div>

                    <textarea
                        value={description}
                        onChange={(e) => handleDescriptionChange(e.target.value)}
                        rows={5}
                        placeholder={t('descriptionPlaceholder')}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#147F26] focus:ring-2 focus:ring-[#147F26]/20 outline-none transition-all resize-none"
                    />
                </div>
            </section>

            {/* Section 3: Price */}
            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-5">
                    <div className="w-8 h-8 bg-[#147F26]/10 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-[#147F26]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h2 className="text-lg font-semibold text-[#147F26]">{t('price')}</h2>
                </div>

                {/* Price Type Toggle */}
                <div className="flex gap-3 mb-5">
                    <button
                        onClick={() => setPriceType('fixed')}
                        className={`flex-1 py-3 px-4 rounded-xl border-2 font-medium transition-all ${
                            priceType === 'fixed'
                                ? 'border-[#147F26] bg-[#147F26]/5 text-[#147F26]'
                                : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                    >
                        {t('fixedPrice')}
                    </button>
                    <button
                        onClick={() => setPriceType('negotiable')}
                        className={`flex-1 py-3 px-4 rounded-xl border-2 font-medium transition-all ${
                            priceType === 'negotiable'
                                ? 'border-[#147F26] bg-[#147F26]/5 text-[#147F26]'
                                : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                    >
                        {t('negotiablePrice')}
                    </button>
                </div>

                {/* Price Input */}
                {priceType === 'fixed' && (
                    <div className="flex gap-3">
                        <div className="flex-1">
                            {isSubmitted && !price && (
                                <div className="flex items-center gap-1 mb-2 text-red-500 text-sm">
                                    <img src={warning_icon} alt="" className="w-4 h-4" />
                                    <span>{t('enterPrice')}</span>
                                </div>
                            )}
                            <input
                                type="number"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                placeholder="0"
                                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#147F26] focus:ring-2 focus:ring-[#147F26]/20 outline-none transition-all"
                            />
                        </div>
                        <div className="w-40">
                            <DropdownUnit
                                text={t('volume_production')}
                                options={units}
                                selected={volumeUnit}
                                onOptionSelect={handleVolumeChange}
                            />
                        </div>
                    </div>
                )}
            </section>

            {/* Section 4: Tags */}
            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 bg-[#147F26]/10 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-[#147F26]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                    </div>
                    <h2 className="text-lg font-semibold text-[#147F26]">{t('tags')}</h2>
                </div>
                <p className="text-sm text-[#7AB30E] mb-4">{t('tagsHint')}</p>

                <div className="flex flex-wrap gap-2 mb-4">
                    {tagsArray.map((tag, index) => (
                        <span
                            key={index}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#147F26]/10 text-[#147F26] rounded-full text-sm font-medium"
                        >
                            {tag}
                            <button
                                onClick={() => handleRemoveTag(index)}
                                className="ml-1 hover:text-[#147F26]/70"
                            >
                                ×
                            </button>
                        </span>
                    ))}
                </div>

                <div className="flex gap-2">
                    <input
                        type="text"
                        value={customTags}
                        onChange={(e) => handleCustomTagsChange(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddTag(customTags)}
                        placeholder={t('addTagPlaceholder')}
                        className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[#147F26] focus:ring-2 focus:ring-[#147F26]/20 outline-none transition-all"
                    />
                    <button
                        onClick={() => handleAddTag(customTags)}
                        className="px-6 py-3 bg-[#147F26] hover:bg-[#0f6820] text-white rounded-xl font-medium transition-colors"
                    >
                        {t('add')}
                    </button>
                </div>
            </section>

            {/* Section 5: Characteristics */}
            {selectedCategory && (
                <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                    <div className="flex items-center gap-2 mb-5">
                        <div className="w-8 h-8 bg-[#147F26]/10 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-[#147F26]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                        </div>
                        <h2 className="text-lg font-semibold text-[#147F26]">{t('characteristics')}</h2>
                    </div>
                    <CharacteristicsFields
                        categoryId={selectedCategory}
                        values={jsonData || {}}
                        onChange={setJsonData}
                    />
                </section>
            )}

            {/* Section 6: Dealer/Marketplace Fields */}
            {isDealerTab && (
                <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                    <div className="flex items-center gap-2 mb-1">
                        <div className="w-8 h-8 bg-[#147F26]/10 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-[#147F26]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                        </div>
                        <h2 className="text-lg font-semibold text-[#147F26]">{t('warehouseInfo')}</h2>
                    </div>
                    <p className="text-sm text-[#7AB30E] mb-5">{t('additionalInfo')}</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium text-[#147F26] mb-2">{t('stockQuantity')}</label>
                            <input
                                type="number"
                                value={stockQuantity}
                                onChange={(e) => setStockQuantity(e.target.value)}
                                placeholder="0"
                                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#147F26] focus:ring-2 focus:ring-[#147F26]/20 outline-none transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-[#147F26] mb-2">{t('minOrderQuantity')}</label>
                            <input
                                type="number"
                                value={minOrderQuantity}
                                onChange={(e) => setMinOrderQuantity(e.target.value)}
                                placeholder="0"
                                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#147F26] focus:ring-2 focus:ring-[#147F26]/20 outline-none transition-all"
                            />
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-[#147F26] mb-2">{t('minOrderPrice')}</label>
                        <input
                            type="number"
                            value={minOrderPrice}
                            onChange={(e) => setMinOrderPrice(e.target.value)}
                            placeholder="0 ₸"
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#147F26] focus:ring-2 focus:ring-[#147F26]/20 outline-none transition-all"
                        />
                    </div>

                    <div className="flex items-center gap-3 mb-4">
                        <input
                            type="checkbox"
                            id="canDeliver"
                            checked={canDeliver}
                            onChange={(e) => setCanDeliver(e.target.checked)}
                            className="w-5 h-5 rounded border-[#147F26]/30 text-[#147F26] focus:ring-[#147F26] focus:ring-2 accent-[#147F26]"
                        />
                        <label htmlFor="canDeliver" className="text-sm font-medium text-[#147F26] cursor-pointer">
                            {t('canDeliver')}
                        </label>
                    </div>

                    {/* Delivery Details - shown when canDeliver is true */}
                    {canDeliver && (
                        <div className="space-y-4 pt-4 border-t border-gray-100">
                            <h4 className="font-medium text-[#147F26] mb-3">{t('deliveryConditions')}</h4>

                            {/* Delivery City */}
                            <div>
                                <label className="block text-sm font-medium text-[#147F26] mb-2">
                                    {t('deliveryCity')}
                                </label>
                                <input
                                    type="text"
                                    value={deliveryCity}
                                    onChange={(e) => setDeliveryCity(e.target.value)}
                                    placeholder="Мысалы: Алматы, Астана, Шымкент"
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#147F26] focus:ring-2 focus:ring-[#147F26]/20 outline-none transition-all"
                                />
                            </div>

                            {/* Delivery Days */}
                            <div>
                                <label className="block text-sm font-medium text-[#147F26] mb-2">
                                    {t('deliveryDays')}
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {weekDays.map((day) => (
                                        <button
                                            key={day.id}
                                            type="button"
                                            onClick={() => toggleDeliveryDay(day.id)}
                                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                                deliveryDays.includes(day.id)
                                                    ? 'bg-[#147F26] text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-[#147F26]/10 hover:text-[#147F26]'
                                            }`}
                                        >
                                            {day.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Delivery Time */}
                            <div>
                                <label className="block text-sm font-medium text-[#147F26] mb-2">
                                    {t('deliveryTime')}
                                </label>
                                <input
                                    type="time"
                                    value={deliveryTime}
                                    onChange={(e) => setDeliveryTime(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#147F26] focus:ring-2 focus:ring-[#147F26]/20 outline-none transition-all"
                                />
                            </div>

                            {/* Delivery Volume */}
                            <div>
                                <label className="block text-sm font-medium text-[#147F26] mb-2">
                                    {t('deliveryVolume')}
                                </label>
                                <input
                                    type="text"
                                    value={deliveryVolume}
                                    onChange={(e) => setDeliveryVolume(e.target.value)}
                                    placeholder="Мысалы: 1-5 тонна, 10-50 қап"
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#147F26] focus:ring-2 focus:ring-[#147F26]/20 outline-none transition-all"
                                />
                            </div>

                            {/* Delivery Duration */}
                            <div>
                                <label className="block text-sm font-medium text-[#147F26] mb-2">
                                    {t('deliveryDuration')}
                                </label>
                                <input
                                    type="text"
                                    value={deliveryDuration}
                                    onChange={(e) => setDeliveryDuration(e.target.value)}
                                    placeholder="Мысалы: 1-2 күн, 3-5 күн"
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#147F26] focus:ring-2 focus:ring-[#147F26]/20 outline-none transition-all"
                                />
                            </div>
                        </div>
                    )}
                </section>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button
                    onClick={handlePublish}
                    disabled={isLoading}
                    className="flex-1 py-4 bg-[#147F26] hover:bg-[#0f6820] disabled:opacity-50 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-[#147F26]/20"
                >
                    {isLoading ? (
                        <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            {t('being_published')}
                        </span>
                    ) : (
                        t('publish')
                    )}
                </button>
                <button
                    onClick={handlePreview}
                    disabled={isLoading}
                    className="flex-1 py-4 border-2 border-[#147F26] hover:bg-[#147F26]/5 text-[#147F26] font-semibold rounded-xl transition-colors"
                >
                    {t('preview')}
                </button>
            </div>
        </div>
    );
}
